import { useContent } from "@/lib/content";
import { useNavigate, useLocation } from "react-router-dom";

const SITEMAP = {
  originals: "GG Originals",
  faucet: "Faucet Collective",
  skate: "Skate",
  goods: "Goods",
};

const prettify = (id) => id.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const SOCIALS = [
  { label: "Instagram", key: "social_instagram" },
  { label: "TikTok", key: "social_tiktok" },
  { label: "X / Twitter", key: "social_twitter" },
  { label: "YouTube", key: "social_youtube" },
  { label: "Contact", key: "social_email", email: true },
];

export default function Footer() {
  const { t } = useContent();
  const navigate = useNavigate();
  const location = useLocation();

  const goToSection = (id) => {
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const sitemapIds = t("collection_order").split(",").map((s) => s.trim()).filter(Boolean);

  return (
    <footer
      data-testid="footer"
      className="bg-[#090909] text-[#F2EFEB] border-t-2 border-[#F2EFEB]"
    >
      <div className="px-4 md:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-6">
          <h3 className="font-display text-6xl md:text-8xl leading-[0.85]">
            GOOD<br />GRIEF<span className="text-[#DFFF00]">.</span>
          </h3>
          <p className="overline mt-6 opacity-60">{t("footer_tagline")}</p>
        </div>

        <div className="md:col-span-3">
          <p className="overline text-[#DFFF00] mb-4">Sitemap</p>
          <ul className="space-y-2 font-mono-ui text-sm">
            {sitemapIds.map((c) => (
              <li key={c}>
                <a
                  href={`/#${c}`}
                  onClick={(e) => { e.preventDefault(); goToSection(c); }}
                  data-testid={`footer-sitemap-${c}`}
                  className="hover:text-[#DFFF00] transition-colors inline-flex items-center gap-1"
                >
                  {SITEMAP[c] || prettify(c)} <span className="opacity-50">↗</span>
                </a>
              </li>
            ))}
            <li>
              <a
                href="/#signup"
                onClick={(e) => { e.preventDefault(); goToSection("signup"); }}
                data-testid="footer-sitemap-signup"
                className="hover:text-[#DFFF00] transition-colors inline-flex items-center gap-1"
              >
                Launch List <span className="opacity-50">↗</span>
              </a>
            </li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <p className="overline text-[#DFFF00] mb-4">Signals</p>
          <ul className="space-y-2 font-mono-ui text-sm">
            {SOCIALS.map(({ label, key, email }) => {
              const val = t(key);
              if (!val) {
                return <li key={key} className="opacity-40" data-testid={`footer-social-${key}-empty`}>{label}</li>;
              }
              const href = email ? `mailto:${val}` : val;
              const linkProps = email ? {} : { target: "_blank", rel: "noopener noreferrer" };
              return (
                <li key={key}>
                  <a
                    href={href}
                    {...linkProps}
                    data-testid={`footer-social-${key}`}
                    className="hover:text-[#DFFF00] transition-colors inline-flex items-center gap-1"
                  >
                    {label} <span className="opacity-50">{email ? "✉" : "↗"}</span>
                  </a>
                </li>
              );
            })}
            <li>
              <a
                data-testid="footer-admin-link"
                href="/admin"
                className="opacity-60 hover:opacity-100 hover:text-[#DFFF00] transition-all"
              >
                Admin
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="px-4 md:px-8 py-5 border-t border-[#F2EFEB]/30 flex flex-col md:flex-row justify-between gap-2 overline opacity-60">
        <span>© {new Date().getFullYear()} Good Grief Worldwide</span>
        <span>{t("footer_rights")}</span>
      </div>
    </footer>
  );
}
